import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CardsFilterBar } from './_components/CardsFilterBar';
import { CardsGrid } from './_components/CardsGrid';
import { cardFiltersSchema, hasActiveFilters } from './_lib/cardFilters';
import { useAdminCardsList } from '../../api/admin-cards';
import { RequirePermission } from '../../auth';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/cards` — admin payout-cards monitoring surface (AUT-126 / ADMIN-14), the
 * FINAL Phase-B admin vertical. Cross-inspector list of bound payout cards.
 *
 * Gated on `process_payouts` (the controller-level permission). The holder
 * name renders via `<RedactedField perm="view_pii">`, agreeing with the
 * server's `piiRevealed` flag; the card cell shows ONLY `•••• last4` — the
 * wire type makes a full PAN / token a compile error.
 *
 * Filters + pagination are URL-state-driven via `useUrlFilters`; cursor
 * pagination bridges the cursor backend to the page-based DataGrid via
 * `useCursorPagination`. Default status filter is `[]` (ALL) — cards is a
 * MONITORING surface, not a triage queue, so the operator lands on the full
 * reconciliation view (NOT a `failed`-only slice like payouts).
 *
 * Force-unbind (status-gated to `active` cards) + its confirm dialog +
 * step-up live inside the shared `<CardsGrid>` — the SAME renderer the
 * inspector-detail Cards tab reuses. Clones `PayoutsListPage`'s shape
 * (observer wrap, filter-reset-on-change effect, RequirePermission
 * outer/inner split) MINUS the row-click drawer (cards has no detail view).
 */
const CardsListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(cardFiltersSchema);
  const pager = useCursorPagination();

  // Any filter change resets pagination to the first page (the cursor space
  // is only valid for the filter set that produced it).
  const filterKey = `${(filters.status ?? []).join(',')}|${filters.inspectorId ?? ''}`;
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable (useCallback); re-run only on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminCardsList({
    ...(filters.status && filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.inspectorId ? { inspectorId: filters.inspectorId } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  // Feed the latest nextCursor into the pager once each fetch settles.
  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('cards.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('cards.subtitle')}
        </Typography>
      </Box>

      <CardsFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <CardsGrid
        rows={items}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('cards.empty')}
          </Typography>
        }
      />
    </Box>
  );
};

const Observed = observer(CardsListPageInner);

/** Whole-page gate: no `process_payouts` → the default 403 panel. */
export const CardsListPage = (): ReactElement => (
  <RequirePermission perm="process_payouts">
    <Observed />
  </RequirePermission>
);
